// What a tank is FOR, separate from what lives in it (the subtype). A
// quarantine or hospital tank holds fish for a few weeks, often bare,
// often with copper or hyposalinity on purpose. Reef ranges, reef chores
// and reef alerts do not fit it: low salinity there is usually the
// treatment working, and no keeper doses alkalinity into a copper tank.
// Pure, shared by the server and /app.

import { PHASE_LOGGED } from './reading-confirm.js';

export const TANK_PURPOSES = ['display', 'quarantine'];

export function tankPurpose(tank) {
  return tank?.purpose === 'quarantine' ? 'quarantine' : 'display';
}

export function isQuarantineTank(tank) {
  return tankPurpose(tank) === 'quarantine';
}

// The tests that still get a HIGH / LOW and an alert in a quarantine tank:
// the ones that hurt a fish within hours. Everything else is logged and
// shown, never graded against a reef range.
export const QUARANTINE_GRADED = ['temp', 'ammonia', 'nitrite', 'ph'];

// Recorded and charted, never graded or alerted, on any tank: a probe
// value with no published range we can cite (feedback #72: ORP from a
// HYDROS, or an Apex, in mV). Its card is the keeper's to add, below the
// graded tests (90: after every starting card's order).
export const HISTORY_ONLY_CARDS = {
  orp: { label: 'ORP', unit: 'mV', order: 90 },
};

// Never an alert, email or page about it, whatever range the keeper gives
// its card: that range colours the card and its Today row only. A keeper's
// ORP range on an undated tank went through the hourly off-band sweep to a
// critical page, SMS through quiet hours (second review, 2026-10-12).
export function neverAlerts(key) {
  return Object.hasOwn(HISTORY_ONLY_CARDS, key);
}

// History only, unless the keeper gave the card a range of their own: then
// their range is theirs to be held to (HIGH / LOW on the card, Today), as it
// was before ORP had a card of ours. Still no score (no scoring row has it)
// and still no alert (neverAlerts).
export function isHistoryOnly(tank, key) {
  if (!Object.hasOwn(HISTORY_ONLY_CARDS, key)) return false;
  const band = tank?.params?.[key]?.acceptable;
  return !(Array.isArray(band) && band.length === 2);
}

// The card patch that adds (or shows again) a history-only card.
export function historyCardPatch(params, key) {
  const card = HISTORY_ONLY_CARDS[key];
  if (!card) return {};
  const have = params && typeof params === 'object' ? params[key] : null;
  if (!have) return { [key]: { ...card, custom: true } };
  return have.hidden ? { [key]: { ...have, hidden: false } } : {};
}

// A param the tank's phase logs without grading (cycling alk / Ca / Mg,
// maturing Ca / Mg) is track_only in the score, so Home must not call it
// LOW and offer a dose for a tank with no corals in it.
export function isGradedParam(tank, key) {
  if (isHistoryOnly(tank, key)) return false;
  if (isCycleQuiet(tank, key)) return false;
  if (isPhaseLogged(tank, key)) return false;
  return !isQuarantineTank(tank) || QUARANTINE_GRADED.includes(key);
}

export function isPhaseLogged(tank, key) {
  return (PHASE_LOGGED[tank?.phase] || []).includes(key);
}

// The cycle's own tests. In a new tank with nothing alive in it, ammonia
// and nitrite rising and falling is the cycle working: they are logged and
// charted, never called high and never alerted, and a water change to
// "fix" them would only slow the cycle. With a fish, coral or invert in
// the tank (a fish-in cycle) they alert like any other test. has_livestock
// must be known to be false: a tank object that does not carry it
// alerts, so a missing field can never silence one.
export const CYCLE_TESTS = ['ammonia', 'nitrite'];

// getPhase (server/score.js) calls a tank "cycling" until the cycle
// reads done or this many days after its start date, whichever is first.
export const CYCLE_MAX_DAYS = 45;

// A cycle often takes 4 to 8 weeks and can stall past that. A tank with
// nothing listed in it whose own tests still show ammonia or nitrite stays
// "cycling" past CYCLE_MAX_DAYS, so its dosed ammonia does not start
// paging on day 45; this is the hard stop, after which it is graded.
export const CYCLE_RUNNING_MAX_DAYS = 90;

// The cycle-done readings must hold on a test at least this many hours
// before the latest one, so a same-evening retest does not count as a
// second day (score.js getPhase, db.js assemblePhaseSignals).
export const CYCLE_CONFIRM_GAP_HOURS = 20;

export function isCycleQuiet(tank, key) {
  return (
    CYCLE_TESTS.includes(key) &&
    tank?.phase === 'cycling' &&
    tank?.has_livestock === false &&
    !isQuarantineTank(tank)
  );
}

// The ammonia and nitrite cards: a quarantine tank gets them when it is
// made one, a new tank gets them for its cycle, and any tank can add them
// in Parameters. Ammonia and nitrite are the daily tests in QT; their zero
// target is the same one the score engine already uses for every tank type
// (DEFAULT_SCORING_CONFIG). A detectable trace alerts as mild below
// severityForOffBand's absolute step, except ammonia in a quarantine tank
// with a resident in it, which is severe at any reading past the band
// (owner decision 2026-10-01).
export const QUARANTINE_PARAMS = {
  ammonia: { label: 'Ammonia', unit: 'ppm', target: 0, acceptable: [0, 0], order: 12 },
  nitrite: { label: 'Nitrite', unit: 'ppm', target: 0, acceptable: [0, 0], order: 14 },
};

// Hidden (not removed) on a NEW quarantine tank: reef chemistry nobody
// tests in QT. The keeper can show them again in Parameters.
export const QUARANTINE_HIDDEN = ['alk', 'calcium', 'magnesium', 'phosphate'];

// The params patch that makes an existing params blob fit a quarantine
// tank. Only adds what is missing; a card the keeper already has keeps
// its own band. `fresh` also hides the reef chemistry (a new tank only:
// switching an existing tank never hides a card the keeper chose). Each
// entry is WHOLE: the stored merge replaces a param's object, it does not
// merge inside it.
export function quarantineParamsPatch(params, { fresh = false } = {}) {
  const have = params && typeof params === 'object' ? params : {};
  const patch = testCardsPatch(have, CYCLE_TESTS, { unhide: true });
  if (fresh) {
    for (const k of QUARANTINE_HIDDEN)
      if (have[k] && !have[k].hidden) patch[k] = { ...have[k], hidden: true };
  }
  return patch;
}

// Adds the standard ammonia / nitrite cards a tank is missing. `unhide`
// also shows one the keeper hid (a QT needs them on screen); without it a
// hidden card stays hidden, because hiding it was the keeper's choice.
export function testCardsPatch(params, keys = CYCLE_TESTS, { unhide = false } = {}) {
  const have = params && typeof params === 'object' ? params : {};
  const patch = {};
  for (const k of keys) {
    const card = QUARANTINE_PARAMS[k];
    if (!card) continue;
    if (!have[k]) patch[k] = { ...card, custom: true };
    else if (unhide && have[k].hidden) patch[k] = { ...have[k], hidden: false };
  }
  return patch;
}

// True for a tank started CYCLE_MAX_DAYS ago or less (or dated ahead),
// which is when getPhase can still call it cycling.
export function isNewForCycle(establishedDate, now = Date.now()) {
  if (!establishedDate) return false;
  const t = new Date(establishedDate).getTime();
  if (!Number.isFinite(t)) return false;
  return (now - t) / 86400000 < CYCLE_MAX_DAYS;
}
